/* eslint-disable react/no-unused-state */
import _ from 'lodash';
import React, {Component} from 'react';
import sc from 'styled-components';

/**
 * this is a complex hackish way to ensure that an antD's table's height
 * is clamped to a height that allows its footer to rest at the bottom
 * edge of the display area.
 *
 * this is a BASE CLASS you can extend at the component level.
 *
 * It uses complex math to determine the proper height of the datagrid (the area under the header
 * and below the footer.
 *
 * This class sets the scrollY state property that must be injected into the tables'
 * scroll property to work properly:
 *
 *           <Table
               id="spotting-group-terms"
               columns={columns}
              ...
               pagination={false}
               dataSource={data}
               loading={isLoading}
               scroll={({ y: scrollY })} <---- from state
               .. />

 *************** Providing height information
 *
 * The setTableHeight() function depends on params set by the higher level component
 * './ProvideSize.js`. You must both extend this class AND decorate this class with
 * the function returned by ProvideSize.
 *
 **************** Freezing height
 *
 * Unfortunately the table component from antd sets a scroll height MAXIMUM past which it
 * scrolls rows. that is it doesn't enforce a minimum height. Worse yet the "empty table"
 * markup is outside the scrolling region, further f**king up the calculus for scrollY.
 *
 * To fix this wrap the render output with freezeSize():
 *
 *
 render() {
    return this.freezeHeight(
      <CategoryView {...this.state} columns={columns} title="Categories" goHome={() => this.goHome()}>
      </CategoryView>
    );
  }
 *
 * this is messy as hell but it does fix these two scenarios: it inforces FIXED (not minimum)
 * height tied to scrollY, AND it floats the "Empty table" boilerplate into absolute space,
 * adjusting the flow of elements to be consistent whether or not the table has rows.
 *
 * All of which to say: the antD table component is really not suited towards rendering a
 * visually managed space in dom - it is better suited to admin-style UIS where spacial control
 * is not particularly important.
 *
 */

export default class TableScroller extends Component {
  componentDidMount() {
    this.unmounted = false;
    this.setTableScroll();
  }

  componentDidUpdate(prevProps) {
    const frameHeight = _.get(this, 'props.contentSize.height', 1000);
    const prevFrameHeight = _.get(prevProps, 'contentSize.height', 1000);
    if (frameHeight !== prevFrameHeight) {
      this.setTableScroll();
    }
  }

  /**
   * Remove event listener
   */
  componentWillUnmount() {
    this.unmounted = true;
  }

  setTableScroll() {
    if (this.unmounted) {
      return;
    }
    try {
      const content = document.getElementById('content-frame');
      let footer = document.getElementsByClassName('ant-table-footer');
      let header = document.getElementsByClassName('ant-table-thead');
      let terms = document.getElementsByClassName('ant-table-wrapper');
      if (!(footer && header && terms)) {
        throw new Error('missing elements');
      }
      footer = footer[0];
      header = header[0];
      terms = terms[0];
      if (!(footer && header && terms)) {
        setTimeout(() => this.setTableScroll(), 2000);
        return;
      }
      const headerHeight = this.heightOf(header)

      const frameHeight = _.get(this, 'props.contentSize.height', 1000); // from the Content/SizeMe
      const footerHeight = this.heightOf(footer);
      const topOfTable = this.topOf(terms);
      const scrollY = Math.max(200, frameHeight - topOfTable - footerHeight - headerHeight - 10);
      // a padding somewhere gets ignored so 10 less
      this.setState({scrollY,});
    } catch (err) {
      setTimeout(() => this.setTableScroll(), 200);
    }
  }

  heightOf(ele){
    if (!ele) return 0;
    if (ele.getBoundingClientRect) {
      return ele.getBoundingClientRect().height;
    }
    return ele.offsetHeight;
  }

  topOf(ele){
    if (!ele) return 0;
    return ele.offsetTop;
  }

  tableSizeFixedEle() {
    const {scrollY} = this.state;

    if (!(scrollY && typeof scrollY === 'number')) {
      return ({children}) => <React.Fragment>
        {children}
      </React.Fragment>
    }
    return sc.div`
    .ant-table-body {
      height: ${scrollY}px
   }
   .ant-table-placeholder{
     position: absolute;
     top: 50px;
     height: 200px;
     width: 100%;
   }
    `
  }

  freezeHeight(content) {
    const Fixed = this.tableSizeFixedEle();
    return <Fixed>
      {content}
    </Fixed>
  }

  // should be overridden
  render() {
    return this.props.children;
  }
}
